import { Table } from "antd";
import { useMemo } from "react";
import { CustomHeaderCell } from "./components/CustomHeaderCell";
import { useProcessedData } from "./hooks/useProcessedData";
import { useTableFilters } from "./hooks/useTableFilters";
import { useTableSort } from "./hooks/useTableSort";
import { CustomProfessionalTableGlobalStyle, HeaderCellWrapper, HeaderTitle } from "./styles";
import type { CustomTableProps, LooseColumn } from "./types";
import { getLooseEllipsis } from "./utils/columnUtils";
import { getColumnBehavior, getSortLabels } from "./utils/tableHelpers";

export function CustomTable<T extends object>(props: CustomTableProps<T>) {
	const {
		columns,
		filterState: externalFilterState,
		onFilterStateChange,
		className,
		tableLayout,
		dataSource,
		...antdTableProps
	} = props;

	const baseColumns = useMemo(() => columns || [], [columns]);

	const {
		filterState,
		openDropdown,
		setOpenDropdown,
		filterSearch,
		filterOptionsByColumn,
		toggleFilterValue,
		handleFilterSearchChange,
	} = useTableFilters<T>({
		baseColumns,
		dataSource: dataSource as T[] | undefined,
		externalFilterState,
		onFilterStateChange,
	});

	const { sortState, handleSortSelect } = useTableSort({
		onSortApplied: () => setOpenDropdown(null),
	});

	const processedData = useProcessedData<T>({
		baseColumns,
		dataSource: dataSource as T[] | undefined,
		filterState,
		sortState,
		searchTerm: "",
	});

	const enhancedColumns = useMemo(
		() =>
			columns?.map((col) => {
				const looseCol = col as LooseColumn<T>;
				const colKey = looseCol.key ?? looseCol.dataIndex;
				const isActionColumn = typeof col.title === "string" && col.title.toLowerCase() === "action";

				if (typeof col.title !== "string" || isActionColumn) {
					return {
						...col,
						title: (
							<HeaderCellWrapper>
								<HeaderTitle>
									{typeof col.title === "string" ? col.title : String(colKey ?? "")}
								</HeaderTitle>
							</HeaderCellWrapper>
						),
					};
				}

				const columnKey = String(colKey || "");
				const { enableSort, enableFilter, sortLabelType } = getColumnBehavior(col);

				return {
					...col,
					sorter: false,
					ellipsis: getLooseEllipsis(looseCol) ?? true,
					showSorterTooltip: false,
					title: () => (
						<CustomHeaderCell
							title={col.title as string}
							columnKey={columnKey}
							enableSort={enableSort}
							enableFilter={enableFilter}
							sortState={sortState}
							openDropdown={openDropdown}
							setOpenDropdown={setOpenDropdown}
							filterOptions={filterOptionsByColumn[columnKey] || []}
							selectedFilters={filterState[columnKey] || []}
							filterSearch={filterSearch[columnKey] || ""}
							onSortSelect={handleSortSelect}
							onFilterSearchChange={handleFilterSearchChange}
							onFilterToggle={toggleFilterValue}
							sortLabels={getSortLabels(sortLabelType)}
						/>
					),
				};
			}),
		[
			columns,
			filterOptionsByColumn,
			filterSearch,
			filterState,
			handleFilterSearchChange,
			handleSortSelect,
			openDropdown,
			setOpenDropdown,
			sortState,
			toggleFilterValue,
		],
	);

	return (
		<>
			<CustomProfessionalTableGlobalStyle />
			<Table
				{...antdTableProps}
				dataSource={processedData}
				columns={enhancedColumns}
				tableLayout={tableLayout ?? "fixed"}
				className={`${className || ""} custom-professional-table`}
			/>
		</>
	);
}

export type { CustomTableColumn, CustomTableProps, TableFilterState } from "./types";

export { getLooseColumnKey } from "./utils/columnUtils";
